// @core/stores/notifications/NotificationsTemplateStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { notificationsApi } from '@core/api';
import type {
  CreateNotificationTemplateRequest,
  NotificationTemplate,
  RenderTemplateRequest,
  RenderTemplateResponse,
  UpdateTemplateRequest,
} from '@core/types/notifications';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class NotificationsTemplateStore {
  // List state
  items: NotificationTemplate[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: NotificationTemplate | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, NotificationTemplate>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): NotificationTemplate | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: NotificationTemplate | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create notification template
   */
  async CreateNotificationTemplate(data: CreateNotificationTemplateRequest): Promise<NotificationTemplate> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await notificationsApi.CreateNotificationTemplate(data);
      const item = response.template;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete notification template
   */
  async DeleteTemplate(template_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await notificationsApi.DeleteTemplate(template_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== template_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === template_id) {
          this.currentItem = null;
        }

        this.removeFromCache(template_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get template by ID
   */
  async GetTemplate(template_id: string): Promise<NotificationTemplate> {
    // Check cache first
    const cached = this.cache.get(template_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await notificationsApi.GetTemplate(template_id);
      const item = response.template;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List notification templates
   */
  async ListNotificationTemplates(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await notificationsApi.ListNotificationTemplates(params);

      runInAction(() => {
        this.items = response.templates?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Render template preview
   */
  async RenderTemplate(template_id: string, data: RenderTemplateRequest): Promise<RenderTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.RenderTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update notification template
   */
  async UpdateTemplate(template_id: string, data: UpdateTemplateRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const notificationsTemplateStore = new NotificationsTemplateStore();
